"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { flushSync } from "react-dom";
import BrandTile from "./BrandTile";
import QuickAddPanel from "../QuickAddPanel";
import {
  DEFAULT_STATE,
  SORTS,
  applyState,
  stateToQuery,
  type BrandShopState,
  type LineChip,
  type SortKey,
  type TypeChip,
} from "../../../lib/brand-shop";
import { whatsappAskUrl } from "../../../lib/contact";
import type { CatalogLite } from "../../../lib/types";

/**
 * The shoppable half of a brand page: a docked filter/sort toolbar and the
 * product grid, all computed on the client from the brand's full product list
 * (the server renders the same initial state from the URL, so a shared link
 * opens filtered with no flash).
 *
 * Changing a filter re-sorts the grid through the View Transitions API, so the
 * tiles that stay slide to their new slot and the rest fade; browsers without
 * it (and reduced-motion users) get an instant swap. The URL mirrors the state
 * with replaceState, so nothing hits the server.
 *
 * The inline quick-add panel is placed exactly as ProductGrid places it (after
 * the last tile in the open tile's row, from the column count).
 */

const MD = "(min-width: 768px)";

function useColumnCount(): number {
  const [cols, setCols] = useState(4);
  useEffect(() => {
    const mq = window.matchMedia(MD);
    const apply = () => setCols(mq.matches ? 4 : 2);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return cols;
}

function reducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap border px-4 py-1.5 text-[11px] uppercase tracking-[0.12em] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${
        active ? "border-ink bg-ink text-paper" : "border-ink/25 hover:border-ink"
      }`}
    >
      {children}
    </button>
  );
}

export default function BrandShop({
  brand,
  path,
  siteUrl,
  products,
  types,
  lines,
  initial,
  brandAskUrl,
}: {
  brand: { slug: string; label: string };
  path: string;
  siteUrl: string;
  products: CatalogLite[];
  types: TypeChip[];
  lines: LineChip[];
  initial: BrandShopState;
  brandAskUrl: string;
}) {
  const [state, setState] = useState<BrandShopState>(initial);
  // Latest state for the window event listener below, without re-binding it.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [stuck, setStuck] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const cols = useColumnCount();

  const shown = useMemo(() => applyState(products, state), [products, state]);

  const update = useCallback(
    (next: BrandShopState) => {
      const commit = () =>
        flushSync(() => {
          setState(next);
          setOpenId(null);
        });
      const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
      if (doc.startViewTransition && !reducedMotion()) doc.startViewTransition(commit);
      else commit();
      window.history.replaceState(window.history.state, "", `${path}${stateToQuery(next)}`);
    },
    [path],
  );

  // The hero rail filters to a line in place (see BrandRail).
  useEffect(() => {
    const onSet = (e: Event) => {
      const d = (e as CustomEvent<{ line?: string; type?: string }>).detail ?? {};
      update({ ...DEFAULT_STATE, sort: stateRef.current.sort, line: d.line, type: d.type });
    };
    window.addEventListener("brand-shop:set", onSet);
    return () => window.removeEventListener("brand-shop:set", onSet);
  }, [update]);

  // Hairline under the toolbar once it is docked at the top.
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), { threshold: 1 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const openIndex = openId ? shown.findIndex((p) => p.id === openId) : -1;
  const openProduct = openIndex >= 0 ? shown[openIndex] : null;
  const panelAfterIndex =
    openIndex >= 0 ? Math.min(Math.floor(openIndex / cols) * cols + cols - 1, shown.length - 1) : -1;

  const total = products.length;
  const countText = shown.length === total ? `${total} ${total === 1 ? "piece" : "pieces"}` : `${shown.length} of ${total} pieces`;
  const filtered = Boolean(state.type || state.line || state.stock);

  return (
    <div>
      <div ref={sentinel} aria-hidden="true" className="h-px" />
      <div className="brand-toolbar sticky top-0 z-30 -mx-6 bg-paper/95 px-6 backdrop-blur-sm" data-stuck={stuck ? "" : undefined}>
        {(types.length > 0 || lines.length > 0) && (
          <div
            role="group"
            aria-label={`Filter ${brand.label}`}
            className="flex items-center gap-2 overflow-x-auto py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {types.length > 0 && (
              <>
                <Chip active={!state.type} onClick={() => update({ ...state, type: undefined })}>
                  All
                </Chip>
                {types.map((t) => (
                  <Chip
                    key={t.slug}
                    active={state.type === t.slug}
                    onClick={() => update({ ...state, type: state.type === t.slug ? undefined : t.slug })}
                  >
                    {t.label} <span className="ml-1 opacity-60">{t.count}</span>
                  </Chip>
                ))}
              </>
            )}
            {types.length > 0 && lines.length > 0 && <span aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-ink/15" />}
            {lines.map((l) => (
              <Chip
                key={l.slug}
                active={state.line === l.slug}
                onClick={() => update({ ...state, line: state.line === l.slug ? undefined : l.slug })}
              >
                {l.label} <span className="ml-1 opacity-60">{l.count}</span>
              </Chip>
            ))}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-ink/10 py-2.5 text-[11px] uppercase tracking-[0.12em] text-ink/60">
          <p aria-live="polite" className="text-ink/70">
            {countText}
            {filtered && (
              <>
                {" "}
                <span aria-hidden="true">·</span>{" "}
                <button type="button" onClick={() => update({ ...DEFAULT_STATE, sort: state.sort })} className="underline underline-offset-4 hover:text-ink">
                  Clear
                </button>
              </>
            )}
          </p>
          <div className="flex items-center gap-5">
            <button
              type="button"
              aria-pressed={state.stock}
              onClick={() => update({ ...state, stock: !state.stock })}
              className="flex min-h-6 items-center gap-2 transition hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            >
              <span aria-hidden="true" className={`inline-block h-3 w-3 border ${state.stock ? "border-ink bg-ink" : "border-ink/40"}`} />
              In stock only
            </button>
            <label className="flex items-center gap-2">
              <span>Sort</span>
              <select
                value={state.sort}
                onChange={(e) => update({ ...state, sort: e.target.value as SortKey })}
                className="min-h-6 border-b border-ink/30 bg-transparent py-0.5 text-[11px] uppercase tracking-[0.12em] text-ink focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                {SORTS.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="border border-ink/15 bg-panel px-6 py-16 text-center md:py-24">
          <p className="display text-[clamp(1.5rem,3.5vw,2.25rem)] uppercase">Nothing in stock right now</p>
          <p className="mx-auto mt-4 max-w-[46ch] text-[14px] leading-relaxed text-ink/70">
            {state.stock
              ? `Every ${brand.label} piece online is sold out at the moment. Message the store on WhatsApp to ask about a restock, or browse the full lineup.`
              : `No ${brand.label} pieces match these filters.`}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {state.stock && (
              <a
                href={brandAskUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-ink px-8 py-3.5 text-[12px] uppercase tracking-[0.18em] text-paper transition hover:bg-gold-deep"
              >
                Ask on WhatsApp
              </a>
            )}
            <button
              type="button"
              onClick={() => update({ ...DEFAULT_STATE, sort: state.sort })}
              className="border border-ink/25 px-8 py-3.5 text-[12px] uppercase tracking-[0.18em] transition hover:border-ink"
            >
              Show all pieces
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 md:grid-cols-4">
          {shown.flatMap((p, i) => {
            const tile = (
              <div key={p.id} style={{ viewTransitionName: `bt-${p.id}` } as CSSProperties}>
                <BrandTile
                  product={p}
                  askUrl={whatsappAskUrl(p.name, `${siteUrl}/shop/${p.slug}`)}
                  isOpen={openId === p.id}
                  onToggleQuickAdd={() => setOpenId((cur) => (cur === p.id ? null : p.id))}
                  priority={i < 2}
                />
              </div>
            );
            if (!openProduct || i !== panelAfterIndex) return [tile];
            return [
              tile,
              <QuickAddPanel key={`panel-${openProduct.id}`} product={openProduct} onClose={() => setOpenId(null)} />,
            ];
          })}
        </div>
      )}
    </div>
  );
}
